import {
  CategoryScale,
  Chart as ChartJS,
  ChartType,
  ChartTypeRegistry,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTheme } from "@chakra-ui/react";
import { OptimusCampaignData } from "../../../../rest/rest";
import annotationPlugin from "chartjs-plugin-annotation";
import { AnnotationOptions } from "chartjs-plugin-annotation";
import { OptimisationLogType } from "../../../../graphql/generated";
import { convertDate } from "./optimusDataGrid";
import { kpiOptionsType } from "./OptimusChartBox";
import { useEffect, useRef, useState } from "react";

type annotationsType = AnnotationOptions;

export interface OptimusChartProps {
  optimusCampaignData?: OptimusCampaignData[];
  optimisations?: OptimisationLogType[];
  error?: Error;
  kpi?: kpiOptionsType[];
}

export const OptimusLineChart = ({
  optimusCampaignData,
  optimisations,
  error,
  kpi,
}: OptimusChartProps) => {
  const chakraTheme = useTheme();
  const xAxis: Date[] = [];
  const yAxis: number[] = [];
  const labels: annotationsType[] = [];
  let optimusCampaignDataDisplay: OptimusCampaignData[] = [];
  let primaryKpi: kpiOptionsType;
  let secondaryKpi: kpiOptionsType;
  const chartRef = useRef<ChartJS | null>(null);
  const [pixelPosition, setPixelPosition] = useState<number>(320);
  let optimusLabelHeight = 320;
  let primaryKpiList: number[];

  useEffect(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current; // Access chartInstance from the ref

      // Ensure chartInstance is available
      if (chartInstance && yAxis.length > 0) {
        const scale = chartInstance.scales["y2"]; // Access the secondary Y-axis scale
        if (scale) {
          const heightValue = scale?.getPixelForValue(optimusLabelHeight);
          if (heightValue >= 312) {
            setPixelPosition(311);
          } else {
            setPixelPosition(heightValue);
          }
        }
      }
    }
  }, [optimusCampaignDataDisplay, optimusLabelHeight]);

  ChartJS.register(
    ChartDataLabels,
    CategoryScale,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    BarController,
    BarElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
  );
  const distinctColors: string[] = Object.values(
    chakraTheme.colors.distinctDataColors
  );

  const illumaColours: string[] = Object.values(chakraTheme.colors.primary);

  if (error || !optimusCampaignData) {
    return <>{"No Data Available for selected filters"}</>;
  }

  if (!kpi || kpi.length < 2) {
    return <>{"Select a KPI"}</>;
  }

  // function to add relevant data to x and yaxis lists of data to be plotted
  const prepareDatasets = () => {
    let datasets: lineDatasetsType[] = [];
    primaryKpi = kpi[0];
    secondaryKpi = kpi[1];

    if (optimusCampaignData && optimusCampaignData.length > 0) {
      optimusCampaignDataDisplay = [...optimusCampaignData];
      optimusCampaignDataDisplay.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();

        return dateA - dateB;
      });
    }

    primaryKpiList = generateKpiList(
      primaryKpi,
      optimusCampaignDataDisplay
    ).kpiList;
    xAxis.push(
      ...generateKpiList(primaryKpi, optimusCampaignDataDisplay).xAxisList
    );
    yAxis.push(
      ...generateKpiList(secondaryKpi, optimusCampaignDataDisplay).kpiList
    );

    // copy ensures order of y axis isnt changed when determining the height of optimus line
    // yaxis height set when campaign data + yaxis is present but also plots it when it is not

    if (yAxis.length > 0) {
      const yAxisCopy = [...yAxis];
      optimusLabelHeight = yAxisCopy.sort()[0];
    } else if (primaryKpiList.length > 0) {
      const p1AxisCopy = [...primaryKpiList];
      optimusLabelHeight = p1AxisCopy.sort()[0];
    }

    datasets = [
      {
        type: "bar" as ChartType,
        label: primaryKpi.value,
        data: primaryKpiList,
        borderColor: illumaColours[2],
        backgroundColor: illumaColours[2],
        borderWidth: 2,
        yAxisID: "y",
        order: 1,
      },
      {
        type: "line" as ChartType,
        label: secondaryKpi.value,
        data: yAxis,
        borderColor: distinctColors[18],
        backgroundColor: distinctColors[18],
        borderWidth: 2,
        yAxisID: "y2",
        order: 0,
      },
    ];
    return { datasets: datasets, optimusLabelHeight: optimusLabelHeight };
  };

  const optimusValues = prepareDatasets();

  const chartData = {
    labels: xAxis,
    datasets: optimusValues["datasets"],
  };

  let contentLines = ["optimisation"];

  optimisations?.forEach((optimisation: any) => {
    if (optimisation.taskName !== null) {
      contentLines = optimisation.taskName.split(/\s/);
    }

    labels.push({
      type: "label",
      color: "white",
      borderColor: distinctColors[0],
      backgroundColor: distinctColors[0],
      borderRadius: 6,
      borderWidth: 2,
      callout: {
        display: true,
        borderColor: distinctColors[0],
        borderDash: [7, 7],
        borderWidth: 2,
      },
      font: [{ size: 12, weight: "light" }],
      content: contentLines,
      position: {
        x: "center",
        y: "start",
      },
      xValue: convertDate(optimisation.createdAt),
      yValue: 0,
      yAdjust: -pixelPosition,
    });
  });

  const chartUnits = (kpi: kpiOptionsType) => {
    if (
      kpi.value === "CTR" ||
      kpi.value === "Viewability" ||
      kpi.value === "VCR"
    ) {
      return {
        callback: (value: any) => {
          return `${value} %`;
        },
      };
    } else if (
      kpi.value === "CPM" ||
      kpi.value === "CPC" ||
      kpi.value === "CPV" ||
      kpi.value === "Media Spend"
    ) {
      return {
        callback: (value: any) => {
          return `£${value}`;
        },
      };
    }
  };

  const options = {
    responsive: true,
    plugins: {
      annotation: {
        annotations: labels,
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Date",
        },
      },
      y: {
        stacked: false,
        display: true,
        position: "left",
        title: {
          display: true,
          text: kpi[0].value,
        },
        ticks: chartUnits(kpi[0]),
        grace: "25%", // allows space at the top of the graph for labels
      },
      y2: {
        stacked: false,
        display: true,
        title: {
          display: true,
          text: kpi[1].value,
        },
        position: "right",
        ticks: chartUnits(kpi[1]),
        grace: "25%",
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
        },
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
  };

  return (
    <>
      <Chart ref={chartRef} type={"bar"} options={options} data={chartData} />
    </>
  );
};

export interface lineDatasetsType {
  type?: keyof ChartTypeRegistry;
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  borderWidth: number;
  yAxisID?: string;
  order?: number;
}

// used to push relevant data for selected Kpi to the graph interface
export const generateKpiList = (
  kpi: kpiOptionsType,
  campaignData: OptimusCampaignData[] = []
) => {
  const kpiList: number[] = [];
  const xAxis: Date[] = [];

  switch (kpi.value) {
    case "Impressions":
      campaignData?.forEach((item: OptimusCampaignData) => {
        {
          (item.impressions || item.impressions === 0) &&
            kpiList.push(item.impressions);
        }
        xAxis.push(item.date);
      });
      break;
    case "Media Spend":
      campaignData?.forEach((item: OptimusCampaignData) => {
        {
          (item.media_cost || item.media_cost === 0) &&
            kpiList.push(item.media_cost);
        }
        xAxis.push(item.date);
      });
      break;
    case "CPC":
      campaignData?.forEach((item: OptimusCampaignData) => {
        {
          (item.cpc || item.cpc === 0) && kpiList.push(item.cpc);
        }
        xAxis.push(item.date);
      });
      break;
    case "CTR":
      campaignData?.forEach((item: OptimusCampaignData) => {
        {
          (item.ctr || item.ctr === 0) && kpiList.push(item.ctr);
        }
        xAxis.push(item.date);
      });
      break;

    case "VCR":
      campaignData?.forEach((item: OptimusCampaignData) => {
        {
          (item.vcr || item.vcr === 0) && kpiList.push(item.vcr);
        }
        xAxis.push(item.date);
      });
      break;
    case "CPM":
      campaignData?.forEach((item: OptimusCampaignData) => {
        {
          (item.cpm || item.cpm === 0) && kpiList.push(item.cpm);
        }
        xAxis.push(item.date);
      });
      break;
  }

  return { kpiList: kpiList, xAxisList: xAxis };
};
